import {Component, Element, h, Prop, State} from '@stencil/core';
import {InformationBoxStyle} from "./InformationBoxStyle";
import {i18n} from '../../utils/I18n';

@Component({
    tag: 'nj-infobox',
    styleUrl: 'nj-infobox.scss',
    shadow: true,
})
export class NjInfobox {

    @Element()
    private element: HTMLElement;

    @State()
    private isOpen = false;

    @State()
    private isHover = false;

    @Prop()
    enableHover = false;

    @Prop()
    handleTabindex?: number;

	@Prop()
	iconStyle: InformationBoxStyle = InformationBoxStyle.ROUNDED;

    @Prop() label?: string;

    @Prop() waAriaLabel?: string;

    /** HTML id Property für die Infobox. */
    @Prop() htmlId?: string;

	/** Prefix für HTML id Property für die Infobox, wird nur verwendet, wenn Property `htmlId` nicht verwendet wird. */
	@Prop() htmlIdPrefix?: string = 'nj-infobox-';

    private contentContainer?: HTMLNjInfoboxContentElement;
    private contentContainerId: string;

    private readonly closeListener = (ev) => this.closeInfobox(ev);
	private readonly escapeListener = (ev) => this.closeOnEscape(ev);
    private mutationObserver?: MutationObserver;

    componentWillLoad() {
        this.contentContainerId = this.htmlId
            ? this.htmlId
            : this.htmlIdPrefix + Math.random().toString(36).substring(2, 6);

        this.contentContainer = document.createElement('nj-infobox-content');
        this.contentContainer.id = this.contentContainerId;
        this.contentContainer.waAriaLabel = this.waAriaLabel;
        this.contentContainer.label = this.label;

        for (let i = 0; i < this.element.children.length; i++) {
            const child = this.element.children[i];
            if (!child.hasAttribute('slot')) {
                this.contentContainer.append(child);
            }
        }
        document.body.append(this.contentContainer);

        if (window.MutationObserver) {
            // If the browser supports it, listen to any DOM changes and update the position if necessary
            this.mutationObserver = new MutationObserver(() => this.updateContentContainerPosition());
            this.mutationObserver.observe(document.body, {childList: true, subtree: true});
        }
    }

    async componentDidLoad() {
        document.addEventListener("click", this.closeListener);
		document.addEventListener('keydown', this.escapeListener);
    }

    componentDidRender() {
        this.updateContentContainerPosition();
    }

    disconnectedCallback() {
        document.removeEventListener("click", this.closeListener);
		document.removeEventListener('keydown', this.escapeListener);
        this.contentContainer?.remove();
        this.mutationObserver?.disconnect();
    }

    private toggleInfobox(ev) {
        ev.preventDefault();
        this.isOpen = !this.isOpen;
        this.updateContentContainerPosition();
    }

    private closeInfobox(ev) {
        if (!ev.composedPath().includes(this.element) && !ev.composedPath().includes(this.contentContainer)) {
            this.isOpen = false;
            this.updateContentContainerPosition();
        }
    }

	private closeOnEscape(ev: KeyboardEvent) {
		if (ev.key === 'Esc' || ev.key === 'Escape') {
			this.isOpen = false;
			this.updateContentContainerPosition();
		}
	}

    private setHover(isHover: boolean) {
        this.isHover = this.enableHover && isHover;
        this.updateContentContainerPosition();
    }

    private updateContentContainerPosition() {
        const handle = this.element?.shadowRoot.querySelector('.infobox-handle');
        if (!handle || !this.contentContainer) {
            return;
        }

        if (this.isOpen || this.isHover) {
            const rect = handle.getBoundingClientRect();
            this.contentContainer.setPosition(
                window.scrollX + rect.x + rect.width,
                window.scrollY + rect.y + rect.height
            );
            this.contentContainer.setOpen(true);
        } else {
            this.contentContainer.setOpen(false);
        }
    }

    render() {
        return (
            <div class="infobox-container" onMouseOver={() => this.setHover(true)} onMouseLeave={() => this.setHover(false)}>
                <button class="infobox-handle"
                    onClick={ev => this.toggleInfobox(ev)}
                    tabindex={this.handleTabindex}
                    aria-pressed={`${(this.isOpen || this.isHover)}`}
                    aria-describedby={this.contentContainerId}>
                    <slot name="infobox-handle">
						{ this.iconStyle !== undefined && this.iconStyle !== null && this.iconStyle === InformationBoxStyle.SQUARE
							? (<information-icon-square width={14} height={14} class="infobox-item infobox-item-square" />)
							: (<info-icon class="infobox-icon" />)}
                        <span class="screen-reader-text">{i18n.getMessage("reservation.additional-information")}</span>
                    </slot>
                </button>
            </div>
        );
    }
}
